import { Specialization } from '@/types/broker-registration.types';
import { FormField } from '../FormField';
import { SelectInput } from '../SelectInput';
import { CheckboxGroup } from '../CheckboxGroup';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  getIndustryOptions,
  getExpertiseOptions,
  getExperienceYearsOptions,
} from '@/utils/broker-registration-options';

interface SpecializationBlockProps {
  specialization: Specialization;
  index: number;
  onUpdate: (index: number, field: keyof Specialization, value: string | string[]) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  errors: Record<string, string>;
  clearError: (field: string) => void;
}

export function SpecializationBlock({
  specialization,
  index,
  onUpdate,
  onRemove,
  canRemove,
  errors,
  clearError,
}: SpecializationBlockProps) {
  const { t } = useLanguage();

  return (
    <div className="p-4 border border-slate-200 rounded-lg mb-4 bg-slate-50 relative">
      <h3 className="text-md font-semibold mb-3 text-[#1D314D]">
        {t('broker_registration.step_two.specializations_title')} #{index + 1}
      </h3>

      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="absolute top-3 right-3 text-red-400 hover:text-red-600 transition duration-150"
          aria-label="Remove specialization"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-3 0h14"
            ></path>
          </svg>
        </button>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label={t('broker_registration.step_two.primary_sector')}
          error={errors[`specialization_${index}_industry`]}
          required
        >
          <SelectInput
            value={specialization.industry}
            onChange={(value) => {
              onUpdate(index, 'industry', value);
              clearError(`specialization_${index}_industry`);
            }}
            options={getIndustryOptions(t)}
            placeholder={t('broker_registration.step_two.primary_sector_placeholder')}
            error={!!errors[`specialization_${index}_industry`]}
          />
        </FormField>

        <FormField
          label={t('broker_registration.step_two.years_experience')}
          error={errors[`specialization_${index}_experienceYears`]}
          required
        >
          <SelectInput
            value={specialization.experienceYears}
            onChange={(value) => {
              onUpdate(index, 'experienceYears', value);
              clearError(`specialization_${index}_experienceYears`);
            }}
            options={getExperienceYearsOptions(t)}
            error={!!errors[`specialization_${index}_experienceYears`]}
          />
        </FormField>
      </div>

      <div className="mt-4">
        <FormField
          label={t('broker_registration.step_two.expertises')}
          helperText={t('broker_registration.step_two.expertises_hint')}
          error={errors[`specialization_${index}_expertises`]}
          required
        >
          <CheckboxGroup
            values={specialization.expertises}
            onChange={(values) => {
              onUpdate(index, 'expertises', values);
              clearError(`specialization_${index}_expertises`);
            }}
            options={getExpertiseOptions(t)}
            columns={3}
            error={!!errors[`specialization_${index}_expertises`]}
          />
        </FormField>
      </div>
    </div>
  );
}
